import { useBlocker } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { claimSmartCreationActionLock } from "./plan-queries";
import { usePlanIdentityReset } from "./use-plan-identity-reset";
import type { PlanSession } from "./use-plan-session";

interface ExitGuardOptions {
  session: PlanSession;
  planId: string;
  canEdit: boolean;
  onClose: () => void | Promise<void>;
}

/**
 * Blocks navigation and page unload while a durable command is in flight or unsaved
 * strategy/Review edits exist, and routes the user through the discard confirmation.
 */
export function useExitGuard({ session, planId, canEdit, onClose }: ExitGuardOptions) {
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const confirmedCloseRef = useRef(false);
  usePlanIdentityReset(session.identity, () => setCloseDialogOpen(false));

  const needsExitConfirmation =
    session.strategySubviewDirty || session.reviewDirty || session.reviewSaving;
  const shouldGuardExit =
    session.actionSerializing || session.structureSaving || (canEdit && needsExitConfirmation);
  const navigationBlocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      if (confirmedCloseRef.current) return false;
      if (!shouldGuardExit) return false;
      return !(
        current.pathname === next.pathname &&
        "smartCreation" in next.search &&
        next.search.smartCreation === planId
      );
    },
    enableBeforeUnload: shouldGuardExit,
    withResolver: true,
  });

  useEffect(() => {
    if (navigationBlocker.status === "blocked") setCloseDialogOpen(true);
  }, [navigationBlocker.status]);

  const completeClose = () => {
    if (navigationBlocker.status === "blocked") {
      navigationBlocker.proceed();
      return;
    }
    confirmedCloseRef.current = true;
    try {
      void Promise.resolve(onClose()).then(
        () => {
          confirmedCloseRef.current = false;
        },
        () => {
          confirmedCloseRef.current = false;
        },
      );
    } catch (error) {
      confirmedCloseRef.current = false;
      throw error;
    }
  };

  const discardChangesAndClose = async () => {
    const { actionLock, currentIdentityRef, mountedRef, structureQueueRef } = session;
    if (structureQueueRef.current?.isPending()) {
      const identity = claimSmartCreationActionLock(
        actionLock,
        currentIdentityRef.current,
        mountedRef.current,
      );
      if (!identity) return;
      session.setActionSerializing(true);
      await structureQueueRef.current.flush();
      if (!mountedRef.current || identity !== currentIdentityRef.current) return;
      session.releaseActionLock(identity);
    }
    session.strategySubviewDirtyRef.current = false;
    session.setStrategySubviewDirty(false);
    session.handleReviewDirtyChange(false);
    setCloseDialogOpen(false);
    completeClose();
  };

  /** Dismissing the dialog also releases a navigation the blocker is holding. */
  const changeCloseDialogOpen = (nextOpen: boolean) => {
    setCloseDialogOpen(nextOpen);
    if (!nextOpen && navigationBlocker.status === "blocked") navigationBlocker.reset();
  };

  return {
    closeDialogOpen,
    changeCloseDialogOpen,
    confirmedCloseRef,
    completeClose,
    discardChangesAndClose,
  };
}

export type ExitGuard = ReturnType<typeof useExitGuard>;
